import { extent } from 'd3';
import { get1DDataXY } from '../../../data/data1d/Spectrum1D/get1DDataXY';
import { isSpectrum2D } from '../../../data/data2d/Spectrum2D';
import nucleusToString from '../../utility/nucleusToString';
import { addToBrushHistory } from '../helper/ZoomHistoryManager';
import { getActiveSpectra } from '../helper/getActiveSpectra';
import { getActiveSpectrum } from '../helper/getActiveSpectrum';
function getActiveData(draft) {
    let data = draft.data.filter((datum) => nucleusToString(datum.info.nucleus) === draft.view.spectra.activeTab &&
        datum.info.dimension === 1);
    // todo: refactor this
    const activeSpectrum = getActiveSpectrum(draft);
    if (activeSpectrum?.id) {
        const activeSpectrumIndex = data.findIndex((datum) => datum.id === activeSpectrum?.id);
        if (activeSpectrumIndex !== -1) {
            const isFid = data[activeSpectrumIndex].info.isFid || false;
            data = data.filter((datum) => datum.info.isFid === isFid);
        }
    }
    else {
        data = data.filter((datum) => !datum.info.isFid);
    }
    return data;
}
function getDomain(draft, options = {}) {
    const { domainSpectraScope = 'visible' } = options;
    let xArray = [];
    let yArray = [];
    const yDomains = {};
    const xDomains = {};
    const data = getActiveData(draft);
    try {
        for (const d of data) {
            const { display, data, id } = d;
            const { y } = get1DDataXY(d);
            const _extent = extent(y);
            const domain = [data.x[0], data.x.at(-1)];
            yDomains[id] = _extent;
            xDomains[id] = domain;
            if (domainSpectraScope === 'all' ||
                (domainSpectraScope === 'visible' && display.isVisible)) {
                xArray = xArray.concat(domain);
                yArray = yArray.concat(_extent);
            }
        }
    }
    catch (error) {
        // TODO: handle error.
        reportError(error);
    }
    return {
        xDomain: xArray?.length > 0 ? extent(xArray) : [],
        yDomain: yArray?.length > 0 ? extent(yArray) : [],
        yDomains,
        xDomains,
    };
}
function get2DDomain(state) {
    let xArray = [];
    let yArray = [];
    const yDomains = {};
    const xDomains = {};
    const { view: { spectra: { activeSpectra, activeTab }, }, data, } = state;
    const nucleus = activeTab.split(',');
    const activeSpectrum = getActiveSpectrum(state);
    const spectrum = data.find((datum) => datum.id === activeSpectrum?.id) || null;
    if (spectrum?.info.isFid) {
        const { minX, maxX, minY, maxY } = spectrum.data.re;
        xArray = [minX, maxX];
        yArray = [minY, maxY];
    }
    else {
        try {
            xArray = data.filter((datum) => isSpectrum2D(datum) &&
                datum.info.nucleus?.join(',') === activeTab &&
                datum.info.isFt).flatMap((datum) => {
                const { minX, maxX } = datum.data.rr;
                return [minX, maxX];
            });
            yArray = data.filter((d) => isSpectrum2D(d) &&
                d.info.nucleus?.join(',') === activeTab &&
                d.info.isFt).flatMap((datum) => {
                const { minY, maxY } = datum.data.rr;
                return [minY, maxY];
            });
        }
        catch (error) {
            // TODO: handle error
            reportError(error);
        }
    }
    const spectraIDs = new Set();
    for (const n of nucleus) {
        const spectra = activeSpectra[n];
        if (spectra?.length === 1) {
            spectraIDs.add(spectra[0].id);
        }
    }
    const filteredData = data
        .filter((d) => spectraIDs.has(d.id) && d.info.dimension === 1)
        .map((datum) => {
        return datum;
    });
    try {
        for (const d of filteredData) {
            const { x, re } = d.data;
            const domain = [x[0], x.at(-1)];
            xDomains[d.id] = domain;
            const _extent = extent(re);
            yDomains[d.id] = _extent;
        }
    }
    catch (error) {
        // TODO: handle error.
        reportError(error);
    }
    return {
        xDomain: extent(xArray),
        yDomain: extent(yArray),
        yDomains,
        xDomains,
    };
}
function setDomain(draft, options) {
    const { updateYDomain = true, isYDomainShared = true, updateXDomain = true, domainSpectraScope, } = options || {};
    let domain;
    if (draft.view.spectra.activeTab) {
        if (draft.displayerMode === '1D') {
            domain = getDomain(draft, { domainSpectraScope });
        }
        else {
            domain = get2DDomain(draft);
        }
        if (updateXDomain) {
            draft.xDomain = domain.xDomain;
            draft.xDomains = domain.xDomains;
        }
        // draft.originDomain = domain;
        if (updateYDomain) {
            draft.yDomain = domain.yDomain;
            if (draft.displayerMode === '1D' && isYDomainShared) {
                draft.yDomains = Object.fromEntries(Object.keys(domain.yDomains).map((key) => {
                    return [key, domain.yDomain];
                }));
            }
            else {
                draft.yDomains = domain.yDomains;
            }
        }
        draft.originDomain = {
            ...draft.originDomain,
            ...(updateXDomain && {
                xDomain: domain.xDomain,
                xDomains: domain.xDomains,
            }),
            ...(updateYDomain && {
                yDomain: domain.yDomain,
                yDomains: domain.yDomains,
            }),
        };
    }
}
function setMode(draft) {
    const { xDomains, view, data, displayerMode } = draft;
    const nuclues = view.spectra.activeTab;
    if (displayerMode === '1D') {
        const datum_ = data.find((datum) => xDomains[datum.id] && nucleusToString(datum.info.nucleus) === nuclues);
        draft.mode = datum_?.info.isFid ? 'LTR' : 'RTL';
    }
    else {
        const activeSpectra = getActiveSpectra(draft);
        let hasFt = false;
        if (Array.isArray(activeSpectra) && activeSpectra?.length > 0) {
            hasFt = activeSpectra.some((spectrum) => !data[spectrum.index].info.isFid);
        }
        else {
            hasFt = data.some((spectrum) => !spectrum.info.isFid &&
                nucleusToString(spectrum.info.nucleus) === nuclues);
        }
        draft.mode = hasFt ? 'RTL' : 'LTR';
    }
}
//action
function handleSetXDomain(draft, action) {
    const xDomain = action.payload.xDomain;
    draft.xDomain = xDomain;
    addToBrushHistory(draft, { xDomain, yDomain: draft.yDomain });
}
//action
function handleSetYDomain(draft, action) {
    const yDomain = action.payload.yDomain;
    draft.yDomain = yDomain;
    addToBrushHistory(draft, { xDomain: draft.xDomain, yDomain });
}
//action
function handleSetAxisDomain(draft, action) {
    const { nucleiPreferences } = action.payload;
    const { originDomain: { xDomain, yDomain }, displayerMode, view: { spectra: { activeTab }, }, } = draft;
    const axisDomain = {};
    const [xNucleus, yNucleus] = activeTab?.split(',') || [];
    for (const nuclei of nucleiPreferences) {
        const { nucleus, axisFrom, axisTo } = nuclei;
        if (nucleus?.toLowerCase() === xNucleus?.toLowerCase()) {
            axisDomain.x = { from: axisFrom, to: axisTo };
        }
        if (nucleus?.toLowerCase() === yNucleus?.toLowerCase()) {
            axisDomain.y = { from: axisFrom, to: axisTo };
        }
    }
    const { x, y } = axisDomain;
    const x1 = x?.from ?? xDomain[0];
    const x2 = x?.to ?? xDomain[1];
    const y1 = y?.from ?? yDomain[0];
    const y2 = y?.to ?? yDomain[1];
    if (displayerMode === '1D') {
        draft.xDomain = [x1, x2];
    }
    else {
        draft.xDomain = [x1, x2];
        draft.yDomain = [y1, y2];
    }
}
function handleMoveOverXAxis(draft, action) {
    const { shiftX, shiftY } = action.payload;
    const [x1, x2] = draft.xDomain;
    const [y1, y2] = draft.yDomain;
    const [x1Origin, x2Origin] = draft.originDomain.xDomain;
    const [y1Origin, y2Origin] = draft.originDomain.yDomain;
    let x1Domain = x1 - shiftX;
    let x2Domain = x2 - shiftX;
    let y1Domain = y1 - shiftY;
    let y2Domain = y2 - shiftY;
    if (x1Domain < x1Origin) {
        x1Domain = x1Origin;
        x2Domain = x2;
    }
    if (x2Domain > x2Origin) {
        x2Domain = x2Origin;
        x1Domain = x1;
    }
    if (y1Domain < y1Origin) {
        y1Domain = y1Origin;
        y2Domain = y2;
    }
    if (y2Domain > y2Origin) {
        y2Domain = y2Origin;
        y1Domain = y1;
    }
    draft.xDomain = [x1Domain, x2Domain];
    draft.yDomain = [y1Domain, y2Domain];
}
export { getDomain, setDomain, setMode, handleSetXDomain, handleSetYDomain, handleMoveOverXAxis, handleSetAxisDomain, };
